import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { ImportPlainRules } from "./ImportPlain";

// ImportGate - whether the file a subscriber picked is the one they meant to pick.
//
// ==================================================================================================
// !! ⭐ THE PHONE KEEPS SEVERAL DATED SNAPSHOTS - AND SINCE 2026-09-24 ANY OF THEM IS SAFE
// ==================================================================================================
//
// `Android/media/com.whatsapp/WhatsApp/Databases/` holds `msgstore.db.crypt15` plus several
// `msgstore-YYYY-MM-DD.1.db.crypt15` beside it. Picking an older one used to be the most dangerous
// mistake in this flow: the upload REPLACED the imported history, so an older snapshot silently
// lost everything after its date. `StaleRefusal` tried to catch that from the file NAME, and was
// only ever called with no date to compare against - so it never fired.
//
// ⭐ THE SERVICE NOW DECIDES FROM THE NEWEST MESSAGE INSIDE EACH DATABASE and keeps every backup that
// holds something the others do not (`ArchiveSet` / `ImportPullRules.PlanFor` in the Bridge). An
// older snapshot is ADDED, never swapped in, so the refusal was retired rather than repaired - a
// guard against a loss that can no longer happen is a false sentence waiting to be shown.
//
// ==================================================================================================
// !! SIZE IS REFUSED BEFORE IT IS LOADED, NOT AFTER
// ==================================================================================================
//
// ⛔ A BROWSER TAB THAT DIES TELLS THE SUBSCRIBER NOTHING. Decryption is one-shot: the ciphertext,
// the plaintext and the inflated database are all in memory at once, so a large file does not run
// slowly - the tab disappears, and on a phone it disappears without a message. A refusal naming the
// size is worth far more than the attempt.
//
// ⚠ TWO THRESHOLDS, BECAUSE "SLOW" AND "IMPOSSIBLE" ARE DIFFERENT ANSWERS. A heavy file is warned
// about and still allowed - it is the subscriber's archive and their decision. Only the refusal is
// a refusal.

/**
 * What the subscriber chose off their own device.
 *
 * ⚠ IT USED TO CARRY A DRIVE `Id` AND GOOGLE'S WORD FOR THE SIZE. The file is now chosen through an
 * ordinary `<input type="file">`, so `Name` and `Bytes` come from the browser's own `File` and there
 * is nothing to look up. ⭐ That deleted the Google Picker, its API key, its cloud project number
 * and the `https://docs.google.com/*` website restriction whose omission fails as "API developer key
 * is invalid" - an error naming the key rather than the missing entry.
 *
 * ⛔ `Data` IS THE BYTES AND `ImportGate` NEVER READS IT. Every rule in this file judges the NAME
 * and the SIZE, which is the whole point of judging before anything is loaded: a refusal naming the
 * size is worth far more than a tab that dies trying.
 */
export class PickedFile {
    public Name: string = "";
    public Bytes: number = 0;
    public Data: Blob | null = null;
    /**
     * "" for a `.crypt15` (the key path). Otherwise WHICH plaintext WhatsApp database the page found
     * when it looked inside the file (`ImportPlainRules.Iphone` / `Android`, set by `AppPlainSniff`) -
     * an iPhone's `ChatStorage.sqlite` has no key and is sent as it is.
     */
    public Plain: string = "";
}

export class ImportGate {
    /** Above this, say it will take a while and suggest a laptop. Still allowed. */
    public static readonly HeavyBytes: number = 60 * 1024 * 1024;

    /** Above this, refused. See the header - the failure mode is a tab that vanishes. */
    public static readonly MaxBytes: number = 250 * 1024 * 1024;

    /**
     * Why this file cannot be used, or "" when it can.
     *
     * ⚠ IT NAMES THE FILE TO PICK INSTEAD, every time. "Wrong file" is not actionable in a folder
     * where eight files have nearly the same name.
     */
    public static FileRefusal = (File: PickedFile): string => {
        if (getCoreUtils().KErrorMode === true) { return "WACommander cannot read a backup now."; }
        try {
            const Name: string = nz(File.Name).trim();
            if (Name.length === 0) { return "No file was chosen."; }
            // ⭐ A PLAINTEXT DATABASE (an iPhone's) WAS JUDGED BY ITS CONTENTS, not its name - the right
            // file in an iPhone backup has a hashed name and no extension. ⚠ The 250 MB ceiling below
            // is NOT applied: that is the memory a one-shot DECRYPTION needs, and this file is never
            // loaded - it is streamed from disk to Dropbox (`ImportPlain`'s header).
            if (nz(File.Plain).length > 0) { return ImportPlainRules.SizeRefusal(File.Bytes); }
            if (/\.crypt15$/i.test(Name) === false) {
                return ImportGate.WrongKind(Name);
            }
            if (File.Bytes <= 0) {
                return "That file is empty. If it was just uploaded from the phone, give it a moment"
                    + " to finish and pick it again.";
            }
            if (File.Bytes > ImportGate.MaxBytes) {
                return "That backup is " + ImportGate.Mb(File.Bytes) + ", which is too large to open"
                    + " in a browser - it would run out of memory part way through. A media-heavy"
                    + " backup usually means the wrong file: the messages are in"
                    + " msgstore.db.crypt15.";
            }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "ImportGate.FileRefusal");
            return "That file could not be checked.";
        }
    };

    /**
     * ⚠ A `.crypt14` IS NAMED SPECIFICALLY, because it sits in the same folder, sorts next to the
     * right file, and is the single most likely wrong pick. Saying "not a crypt15" to somebody
     * looking at a file called `msgstore.db.crypt14` is telling them what they can already see.
     */
    private static WrongKind = (Name: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "That file cannot be used."; }
        try {
            if (/\.crypt14$/i.test(Name) === true) {
                return "That is an older crypt14 backup, which uses a different key and cannot be"
                    + " opened with your 64-digit key. Pick msgstore.db.crypt15 instead - it is in"
                    + " the same folder.";
            }
            if (/\.mcrypt1$/i.test(Name) === true) {
                return "That is a single media file, not the message database. Pick"
                    + " msgstore.db.crypt15.";
            }
            return "That is not a WhatsApp message backup. Pick the file called"
                + " msgstore.db.crypt15 - or one named msgstore-<date>.1.db.crypt15 if you want an"
                + " older day.";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "ImportGate.WrongKind");
            return "That file cannot be used.";
        }
    };

    /** Whether to say this will take a while. Not a refusal - it is their archive. */
    public static IsHeavy = (File: PickedFile): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (File.Bytes <= ImportGate.HeavyBytes) { return false; }
            return File.Bytes <= ImportGate.MaxBytes;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "ImportGate.IsHeavy");
            return false;
        }
    };

    /** A size a person can read. Whole MB - a backup is never usefully described to one decimal. */
    public static Mb = (Bytes: number): string => {
        if (getCoreUtils().KErrorMode === true) { return "?"; }
        try {
            const N: number = Number(Bytes);
            if (isNaN(N) === true || N <= 0) { return "0 MB"; }
            if (N < 1048576) { return "under 1 MB"; }
            return String(Math.round(N / 1048576)) + " MB";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "ImportGate.Mb");
            return "?";
        }
    };
}
